// Sidebar navigation preferences — pure logic shared by the settings panel
// and the Sidebar renderer. The persisted shape is minimal and versioned by
// the storage key (see navigationPreferencesStore): the order of group keys,
// the order of item routes per group, and the set of hidden item routes.
// Anything unknown in storage is dropped on read, so removing/renaming a nav
// entry can never break the sidebar — it just falls back to code order.

import { NAV_GROUPS, type NavGroup, type NavItem } from '../../components/layout/nav-items.js';

export interface NavigationPreferences {
  /** Group keys in the user's preferred order (subset is fine). */
  groupOrder: readonly string[];
  /** Hidden item routes (across all groups). */
  hiddenItems: readonly string[];
  /** Item routes in the user's preferred order, per group key. */
  itemOrder: Readonly<Record<string, readonly string[]>>;
}

export const DEFAULT_NAVIGATION_PREFERENCES: NavigationPreferences = {
  groupOrder: NAV_GROUPS.map((group) => group.key),
  hiddenItems: [],
  itemOrder: {},
};

export function isKnownGroupKey(key: string): boolean {
  return NAV_GROUPS.some((group) => group.key === key);
}

export function isKnownItemRoute(route: string): boolean {
  return NAV_GROUPS.some((group) => group.items.some((item) => item.to === route));
}

/**
 * Validates unknown JSON as navigation preferences: only known groups/routes
 * survive; malformed shapes fall back to the defaults.
 */
export function parseNavigationPreferences(raw: unknown): NavigationPreferences {
  if (typeof raw !== 'object' || raw === null) {
    return DEFAULT_NAVIGATION_PREFERENCES;
  }

  const candidate = raw as {
    groupOrder?: unknown;
    hiddenItems?: unknown;
    itemOrder?: unknown;
  };

  const isStringArray = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.every((entry) => typeof entry === 'string');

  const groupOrder = isStringArray(candidate.groupOrder)
    ? candidate.groupOrder.filter((key): key is string => isKnownGroupKey(key as string))
    : [];

  const hiddenItems = isStringArray(candidate.hiddenItems)
    ? candidate.hiddenItems.filter((route): route is string => isKnownItemRoute(route as string))
    : [];

  const itemOrder: Record<string, readonly string[]> = {};
  if (typeof candidate.itemOrder === 'object' && candidate.itemOrder !== null) {
    for (const [groupKey, value] of Object.entries(
      candidate.itemOrder as Record<string, unknown>,
    )) {
      if (!isKnownGroupKey(groupKey) || !Array.isArray(value)) continue;
      const groupItems = NAV_GROUPS.find((group) => group.key === groupKey)?.items ?? [];
      const validRoutes = value.filter(
        (route): route is string =>
          typeof route === 'string' && groupItems.some((item) => item.to === route),
      );
      if (validRoutes.length > 0) itemOrder[groupKey] = validRoutes;
    }
  }

  return { groupOrder, hiddenItems, itemOrder };
}

function orderItems(
  preferred: readonly string[] | undefined,
  items: readonly NavItem[],
): NavItem[] {
  if (preferred === undefined) return [...items];
  const byRoute = new Map(items.map((item) => [item.to, item]));
  const ordered: NavItem[] = [];
  for (const route of preferred) {
    const item = byRoute.get(route);
    if (item !== undefined) {
      ordered.push(item);
      byRoute.delete(route);
    }
  }
  // Routes added after the preference was saved keep their code order at the end.
  ordered.push(...byRoute.values());
  return ordered;
}

/**
 * Applies preferences to the groups: reorders groups, reorders items inside
 * each group and drops hidden items. Groups whose items are all hidden
 * disappear. Role/flag filtering is NOT done here — the Sidebar keeps owning
 * it (preferences only know about the user's own choices).
 */
export function applyNavigationPreferences(
  groups: readonly NavGroup[],
  preferences: NavigationPreferences,
): NavGroup[] {
  const hidden = new Set(preferences.hiddenItems);
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const orderedKeys: string[] = [];
  for (const key of preferences.groupOrder) {
    if (byKey.has(key)) orderedKeys.push(key);
  }
  // Groups unknown to the stored order (new since the save) keep code order.
  for (const group of groups) {
    if (!orderedKeys.includes(group.key)) orderedKeys.push(group.key);
  }

  return orderedKeys
    .map((key) => {
      const group = byKey.get(key)!;
      const items = orderItems(preferences.itemOrder[key], group.items).filter(
        (item) => !hidden.has(item.to),
      );
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}
